import { IS_AWS_MODE } from '../config/frontend';
import { getSharedAuthToken, getSharedAuthRevision } from './sharedAuthState';

// Tab-applied request token.
//
// This module tracks the single IdToken (and its shared mutation revision) that
// THIS tab has actually applied to React identity, axios Authorization, and
// authenticated requests. It is NOT the browser-wide shared signal — that lives
// in localStorage.token plus the shared revision. Keeping the two separate
// prevents an authenticated request from being authorized with a shared token
// this tab has not yet adopted (cross-tab StorageEvent delivery lag), and the
// revision check additionally detects token ABA even when a StorageEvent has not
// yet been delivered.

let appliedIdToken: string | null = null;
let appliedRevision: string | null = null;

export const setAppliedAwsIdToken = (idToken: string | null, revision: string | null = null): void => {
  appliedIdToken = idToken;
  appliedRevision = revision;
};

export const getAppliedAwsIdToken = (): string | null => appliedIdToken;
export const getAppliedAwsRevision = (): string | null => appliedRevision;

// Fail-closed AWS request token. Returns the tab-applied IdToken ONLY when it is
// still the browser-wide shared token AND the shared revision is still the
// revision this tab applied. Otherwise returns null, so an authenticated request
// is blocked rather than sent under a token this tab has not adopted (or that
// was replaced and then restored via an ABA transition). It NEVER returns a
// newer shared token before AuthContext has adopted it.
export const getAwsRequestIdToken = (): string | null => {
  if (appliedIdToken === null) return null;
  if (getSharedAuthToken() !== appliedIdToken) return null;
  if (getSharedAuthRevision() !== appliedRevision) return null;
  return appliedIdToken;
};

// Central request-token accessor for authenticated request boundaries.
// AWS mode uses the tab-applied fail-closed token; non-AWS (demo) mode keeps the
// legacy localStorage token untouched.
export const getRequestAuthToken = (): string | null =>
  IS_AWS_MODE ? getAwsRequestIdToken() : getSharedAuthToken();
